class DDPDPLightbox extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.handledNativeCloseEvents = 0;

    this.dialog = this.querySelector('[data-dd-pdp-lightbox-dialog]');
    this.surface = this.querySelector('[data-dd-pdp-lightbox-surface]');
    this.scrollArea = this.querySelector('[data-dd-pdp-lightbox-scroll]');
    this.closeButton = this.querySelector('[data-dd-pdp-lightbox-close]');
    this.quickViewPanel = this.querySelector('[data-dd-pdp-quick-view-panel]');
    this.quickViewContent = this.querySelector('[data-dd-pdp-quick-view-content]');
    this.reviewsPanel = this.querySelector('[data-dd-pdp-reviews-panel]');
    this.reviewsStatus = this.querySelector('[data-dd-pdp-reviews-status]');
    this.reviewsWidget = this.querySelector('[data-dd-pdp-reviews-widget]');
    this.reviewsTitle = this.reviewsPanel?.querySelector('h2[id]');
    this.productId = this.dataset.productId || 'aggregate';
    this.isAggregateReviews = this.hasAttribute('data-dd-reviews-lightbox-generic');
    this.hasReviewsWidget = this.reviewsPanel?.dataset.ddHasReviewsWidget === 'true';
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (!this.dialog || !this.surface || !this.closeButton) return;

    this.closeButton.addEventListener('click', () => this.close(), { signal });
    this.dialog.addEventListener('cancel', (event) => this.onCancel(event), { signal });
    this.dialog.addEventListener('close', () => this.onNativeClose(), { signal });
    this.dialog.addEventListener('click', (event) => this.onDialogClick(event), { signal });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    document.addEventListener('stamped:reviews:loaded', () => this.onStampedReviewsLoaded(), { signal });
    window.addEventListener('stamped:reviews:loaded', () => this.onStampedReviewsLoaded(), { signal });
    document.addEventListener('stamped:widget:full-page:loaded', () => this.onStampedReviewsLoaded(), { signal });
    document.addEventListener('stamped:reviews:widget:loaded', () => this.onStampedReviewsLoaded(true), { signal });

    this.observeReviewsWidget();
    this.configureStampedWidget();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.reviewsObserver?.disconnect();
    this.blendRatingsObserver?.disconnect();
    this.clearReviewsTimer();
    if (this.dialog?.open) this.close(false);
    else this.releaseOverlay();
    this.initialized = false;
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-dd-open-quick-view]');
    if (!trigger || trigger.dataset.ddQuickViewDialog !== this.dialog.id) return;
    event.preventDefault();
    this.openQuickView(trigger);
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.close();
  }

  onCancel(event) {
    event.preventDefault();
    this.close();
  }

  openQuickView(trigger) {
    const template = document.getElementById(trigger.dataset.ddQuickViewTemplate);
    if (!(template instanceof HTMLTemplateElement) || !this.quickViewContent || !this.quickViewPanel) return;

    const content = template.content.cloneNode(true);
    const openComparison = trigger.dataset.ddQuickViewTab === 'compare';
    if (openComparison) {
      // Direct comparison entry should not fetch the unused full-size details image.
      const details = content.querySelector('[data-dd-pdp-details-panel]');
      if (details) {
        details.hidden = true;
        details.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; });
      }
    }
    this.blendRatingsObserver?.disconnect();
    this.quickViewContent.replaceChildren(content);
    if (!openComparison) this.initializeGallery(this.quickViewContent);
    this.initializeBlendRatings(this.quickViewContent);
    this.initializeDisclosures(this.quickViewContent);
    this.initializeComparison(this.quickViewContent);
    this.quickViewPanel.hidden = false;
    if (this.reviewsPanel) this.reviewsPanel.hidden = true;
    this.activeView = 'quick-view';

    const title = this.quickViewContent.querySelector('[data-dd-pdp-lightbox-title]');
    if (title?.id) this.dialog.setAttribute('aria-labelledby', title.id);
    if (openComparison) {
      this.quickViewContent.querySelector('[data-dd-pdp-comparison-tabs] [role="tab"][aria-selected="false"]')?.click();
    }
    this.open(trigger);
  }

  initializeGallery(container) {
    const gallery = container.querySelector('[data-dd-gallery]');
    const template = gallery?.querySelector('[data-dd-gallery-images]');
    if (!template || gallery.dataset.ddGalleryInitialized === 'true') return;
    const images = [...template.content.querySelectorAll('img')];
    if (images.length < 2) return;
    gallery.dataset.ddGalleryInitialized = 'true';
    const stage = gallery.querySelector('[data-dd-gallery-stage]');
    const current = gallery.querySelector('[data-dd-gallery-current]');
    let index = 0;
    let request = 0;
    const loaded = new Map();
    const prepare = (position) => {
      position = (position + images.length) % images.length;
      if (!loaded.has(position)) {
        const image = document.importNode(images[position], true);
        image.loading = 'eager';
        const ready = image.decode().then(() => image).catch((error) => {
          loaded.delete(position);
          throw error;
        });
        loaded.set(position, ready);
      }
      return loaded.get(position);
    };
    const preload = () => {
      prepare(index + 1).catch(() => {});
      prepare(index - 1).catch(() => {});
    };
    const show = async (next) => {
      const target = (next + images.length) % images.length;
      const token = ++request;
      index = target;
      stage.setAttribute('aria-busy', 'true');
      try {
        const image = await prepare(target);
        if (token !== request || !gallery.isConnected) return;
        // Keep the current image visible until its replacement is fully decoded.
        stage.replaceChildren(image);
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          image.animate([{ opacity: 0.35 }, { opacity: 1 }], {
            duration: 220, easing: 'ease-out',
          });
        }
        current.textContent = String(target + 1);
        thumbnails.forEach((button, position) => button.setAttribute('aria-pressed', String(position === target)));
        preload();
      } catch (_error) {
        if (token === request) index = Number(current.textContent) - 1;
      } finally {
        if (token === request) stage.removeAttribute('aria-busy');
      }
    };
    const rail = gallery.querySelector('[data-dd-gallery-thumbnails]');
    const thumbnails = images.map((image, position) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dd-pdp-gallery-thumbnail';
      button.setAttribute('aria-label', `${gallery.dataset.thumbnailLabel} ${position + 1}`);
      button.setAttribute('aria-pressed', String(position === 0));
      const thumb = image.cloneNode(true);
      const source = new URL(thumb.src, document.baseURI);
      source.searchParams.set('width', '144');
      thumb.removeAttribute('srcset');
      thumb.removeAttribute('sizes');
      thumb.src = source.href;
      thumb.alt = '';
      thumb.loading = 'lazy';
      button.append(thumb);
      button.addEventListener('click', () => show(position));
      rail?.append(button);
      return button;
    });
    preload();
    gallery.querySelector('[data-dd-gallery-prev]').addEventListener('click', () => show(index - 1));
    gallery.querySelector('[data-dd-gallery-next]').addEventListener('click', () => show(index + 1));
    gallery.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      show(index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    let start;
    stage.addEventListener('touchstart', (event) => {
      start = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      if (!start) return;
      const dx = event.changedTouches[0].clientX - start.x;
      const dy = event.changedTouches[0].clientY - start.y;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) show(index + (dx < 0 ? 1 : -1));
      start = null;
    }, { passive: true });
    stage.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    gallery.querySelector('[data-dd-gallery-controls]').hidden = false;
  }

  initializeBlendRatings(container) {
    this.blendRatingsObserver?.disconnect();
    const pending = new Set(container.querySelectorAll('[data-dd-blend-rating]'));
    const populate = () => {
      for (const target of pending) {
        // Each inert comparison template carries its own Stamped summary, so
        // ratings do not depend on other badges surviving a product-size swap.
        const embeddedBadge = target.querySelector('.stamped-badge');
        const source = [...document.querySelectorAll('.stamped-product-reviews-badge[data-id]')]
          .find((badge) => badge.dataset.id === target.dataset.ddBlendRating && badge.querySelector('.stamped-badge-starrating'));
        const badge = embeddedBadge || source?.querySelector('.stamped-badge');
        if (!badge) continue;
        const stars = badge.querySelector('.stamped-badge-starrating');
        const reviews = badge.querySelector('[data-reviews]')?.dataset.reviews;
        if (!stars || !reviews) continue;
        const count = document.createElement('span');
        count.className = 'dd-pdp-blend-guide__review-count';
        count.textContent = `(${reviews})`;
        count.setAttribute('aria-hidden', 'true');
        target.replaceChildren(stars.cloneNode(true), count);
        target.hidden = false;
        pending.delete(target);
      }
      if (!pending.size) this.blendRatingsObserver?.disconnect();
    };
    populate();
    if (pending.size) {
      this.blendRatingsObserver = new MutationObserver(populate);
      this.blendRatingsObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  initializeComparison(container) {
    const comparison = container.querySelector('[data-dd-pdp-comparison]');
    if (!comparison) return;
    const source = document.getElementById(comparison.dataset.comparisonTemplate);
    const tablist = comparison.querySelector('[data-dd-pdp-comparison-tabs]');
    const details = comparison.querySelector('[data-dd-pdp-details-panel]');
    const guide = comparison.querySelector('[data-dd-pdp-compare-panel]');
    if (!(source instanceof HTMLTemplateElement) || !tablist || !details || !guide) return;
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    if (tabs.length !== 2) return;

    const select = (index) => {
      // The inert guide is cloned only on demand; its images do not load on page view.
      if (index === 1 && !guide.childElementCount) {
        guide.append(source.content.cloneNode(true));
        this.initializeBlendRatings(this.quickViewContent);
      }
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      details.hidden = index === 1;
      guide.hidden = index !== 1;
      if (index === 0) this.initializeGallery(details);
      const title = (index === 1 ? guide : details).querySelector('[data-dd-pdp-comparison-title], [data-dd-pdp-lightbox-title]');
      if (title?.id) this.dialog.setAttribute('aria-labelledby', title.id);
      this.scrollArea?.scrollTo({ top: 0, behavior: 'auto' });
    };
    // Listeners belong to this cloned view and are collected when it is discarded.
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', (event) => {
        let target;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') target = 1 - index;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;
        event.preventDefault();
        select(target);
        tabs[target].focus({ preventScroll: true });
      });
    });
    tablist.hidden = false;
  }

  openReviews(trigger) {
    if (!this.reviewsPanel) return false;

    this.clearReviewsTimer();
    const generation = (this.reviewsLoadGeneration || 0) + 1;
    this.reviewsLoadGeneration = generation;

    if (this.quickViewPanel) this.quickViewPanel.hidden = true;
    this.quickViewContent?.replaceChildren();
    this.reviewsPanel.hidden = false;
    this.activeView = 'reviews';

    if (this.reviewsTitle?.id) this.dialog.setAttribute('aria-labelledby', this.reviewsTitle.id);
    this.prepareReviewsState();
    this.open(trigger);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.initializeReviews(0, generation));
    });
    return true;
  }

  open(trigger) {
    this.openedBy = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.preservedCart = this.hasAttribute('data-dd-preserve-cart') ? this.closest('cart-drawer.active') : null;
    if (this.preservedCart) {
      this.preservedCart.focusRequestId = (this.preservedCart.focusRequestId || 0) + 1;
      window.clearTimeout(this.preservedCart.focusAfterCartUpdateTimer);
      window.clearTimeout(this.preservedCart.focusConfirmationTimer);
    }
    this.closeCompetingOverlays();
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    this.scrollArea?.scrollTo({ top: 0, behavior: 'auto' });

    if (!this.dialog.open) {
      this.ownsBodyOverflowLock = !document.body.classList.contains('overflow-hidden');
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
    }

    document.body.classList.add('overflow-hidden');
    const focusRequestId = (this.focusRequestId || 0) + 1;
    this.focusRequestId = focusRequestId;
    window.requestAnimationFrame(() => {
      if (this.focusRequestId !== focusRequestId || !this.dialog.open) return;
      if (typeof trapFocus === 'function') trapFocus(this.dialog, this.closeButton);
      this.closeButton.focus({ preventScroll: true });
    });
  }

  close(restoreFocus = true) {
    if (!this.dialog?.open) return;
    this.restoreFocusAfterClose = restoreFocus;
    this.focusRequestId = (this.focusRequestId || 0) + 1;
    if (typeof this.dialog.close === 'function') {
      this.handledNativeCloseEvents = (this.handledNativeCloseEvents || 0) + 1;
      this.dialog.close();
    } else this.dialog.removeAttribute('open');
    this.onClosed();
  }

  onNativeClose() {
    if (this.dialog?.open) {
      if (this.handledNativeCloseEvents > 0) this.handledNativeCloseEvents -= 1;
      return;
    }
    if (this.handledNativeCloseEvents > 0) {
      this.handledNativeCloseEvents -= 1;
      return;
    }
    this.onClosed();
  }

  onClosed() {
    this.reviewsLoadGeneration = (this.reviewsLoadGeneration || 0) + 1;
    this.clearReviewsTimer();
    if (this.isAggregateReviews && !this.stampedHasRenderedContent()) {
      this.widgetInitialised = false;
      this.loadedProductId = undefined;
    }
    this.releaseOverlay();

    const focusTarget =
      this.restoreFocusAfterClose !== false && this.openedBy instanceof HTMLElement && document.contains(this.openedBy)
        ? this.openedBy
        : null;
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    if (this.preservedCart?.classList.contains('active') && this.restoreFocusAfterClose !== false) {
      const panel = this.preservedCart.querySelector('.drawer__inner');
      if (panel) this.preservedCart.installDrawerFocusTrap(panel, focusTarget || panel.querySelector('.drawer__close') || panel);
    } else {
      focusTarget?.focus({ preventScroll: true });
    }

    this.restoreFocusAfterClose = true;
    this.activeView = undefined;
    this.openedBy = undefined;
    this.preservedCart = undefined;
    if (this.quickViewPanel) this.quickViewPanel.hidden = true;
    if (this.reviewsPanel) this.reviewsPanel.hidden = true;
    this.quickViewContent?.replaceChildren();
  }

  releaseOverlay() {
    if (this.ownsBodyOverflowLock) document.body.classList.remove('overflow-hidden');
    this.ownsBodyOverflowLock = false;
  }

  closeCompetingOverlays() {
    document.querySelectorAll('details-modal.header__search').forEach((searchModal) => {
      if (typeof searchModal.close === 'function' && searchModal.isOpen?.()) searchModal.close(false);
    });

    document.querySelectorAll('#Details-HeaderMenu-DD-Shop[open]').forEach((details) => {
      const megaMenu = details.querySelector('dd-mega-menu');
      if (typeof megaMenu?.close === 'function') megaMenu.close(false);
      else details.removeAttribute('open');
    });

    const mobileMenu = document.querySelector('header-drawer');
    if (mobileMenu?.mainDetailsToggle?.hasAttribute('open')) {
      mobileMenu.closeMenuDrawer?.(null, false);
      mobileMenu.mainDetailsToggle.removeAttribute('open');
    }

    const cartDrawer = document.querySelector('cart-drawer.active');
    if (cartDrawer !== this.preservedCart && typeof cartDrawer?.close === 'function') cartDrawer.close();

    document.querySelectorAll('product-modal[open], modal-dialog[open]').forEach((modal) => {
      if (typeof modal.hide === 'function') modal.hide();
      else modal.removeAttribute('open');
    });
  }

  initializeDisclosures(container) {
    container.querySelectorAll('details > summary').forEach((summary) => {
      const details = summary.parentElement;
      summary.setAttribute('aria-expanded', String(details.hasAttribute('open')));
      summary.addEventListener('click', () => {
        summary.setAttribute('aria-expanded', String(!details.hasAttribute('open')));
      });
    });
  }

  observeReviewsWidget() {
    if (!this.reviewsWidget || !this.hasReviewsWidget) return;
    this.reviewsObserver = new MutationObserver(() => {
      this.configureStampedWidget();
      if (this.stampedHasRenderedContent()) this.markReviewsReady();
    });
    this.reviewsObserver.observe(this.reviewsWidget, { childList: true, subtree: true });
  }

  getStampedRoot() {
    return this.reviewsWidget?.querySelector('#stamped-main-widget, #stamped-reviews-widget') || null;
  }

  configureStampedWidget() {
    const stampedRoot = this.getStampedRoot();
    if (!stampedRoot) return;
    if (stampedRoot.id === 'stamped-main-widget') stampedRoot.dataset.widgetType = 'standard';
    if (this.isAggregateReviews) stampedRoot.dataset.minRatingReviews = '1';
    stampedRoot.dataset.animation = 'false';
  }

  stampedHasRenderedContent() {
    const stampedRoot = this.getStampedRoot();
    if (!stampedRoot) return false;
    if (this.isAggregateReviews) {
      // Stamped inserts an empty wrapper, styles and scripts before its actual cards.
      return Boolean(stampedRoot.querySelector('.stamped-ratings-wrapper[id^="stamped-review-"], [data-dd-reviews-empty]'));
    }
    return stampedRoot.childElementCount > 0 || stampedRoot.textContent.trim().length > 0;
  }

  setAggregateReviewsReady(ready) {
    if (!this.isAggregateReviews || !this.reviewsWidget) return;
    this.reviewsWidget.dataset.ddReviewsReady = String(ready);
    // Keep layout measurable while preventing interaction with a partial render.
    this.reviewsWidget.inert = !ready;
  }

  prepareReviewsState() {
    if (!this.reviewsPanel || !this.reviewsStatus) return;
    if (!this.hasReviewsWidget) {
      this.markReviewsUnavailable();
      return;
    }
    if (this.stampedHasRenderedContent()) {
      this.markReviewsReady();
      return;
    }
    this.reviewsPanel.setAttribute('aria-busy', 'true');
    this.setAggregateReviewsReady(false);
    this.reviewsStatus.textContent = window.ddReviewsLightboxStrings?.loading || this.reviewsStatus.textContent;
    this.reviewsStatus.hidden = false;
  }

  initializeReviews(attempt = 0, generation = this.reviewsLoadGeneration) {
    if (generation !== this.reviewsLoadGeneration || !this.hasReviewsWidget || this.activeView !== 'reviews' || !this.dialog.open) return;

    this.configureStampedWidget();
    if (this.stampedHasRenderedContent()) {
      this.markReviewsReady();
      return;
    }

    if (!this.widgetInitialised || this.loadedProductId !== this.productId) {
      // The aggregate widget only needs display widgets, not a global PDP/badge reload.
      const loadReviews = this.isAggregateReviews && typeof window.StampedFn?.loadDisplayWidgets === 'function'
        ? window.StampedFn.loadDisplayWidgets
        : window.StampedFn?.reloadUGC;
      if (typeof loadReviews === 'function') {
        try {
          loadReviews.call(window.StampedFn);
          this.widgetInitialised = true;
          this.loadedProductId = this.productId;
        } catch (_error) {
          this.widgetInitialised = false;
        }
      }
    }

    if (attempt >= 40) {
      this.markReviewsUnavailable();
      this.widgetInitialised = false;
      return;
    }

    this.reviewsRetryTimer = window.setTimeout(() => this.initializeReviews(attempt + 1, generation), 250);
  }

  onStampedReviewsLoaded(displayCompleted = false) {
    this.configureStampedWidget();
    if (this.stampedHasRenderedContent()) this.markReviewsReady();
    else if (displayCompleted) this.restoreAggregateEmptyControls();
  }

  restoreAggregateEmptyControls() {
    if (!this.isAggregateReviews || !this.aggregateControlsTemplate) return;
    const stampedRoot = this.getStampedRoot();
    const wrapper = stampedRoot?.querySelector('.stamped-reviews-wrapper');
    if (!wrapper) return;

    // Stamped's empty Site Reviews response drops its own tabs. Keep a route back.
    if (!stampedRoot.querySelector('.stamped-widget-buttons')) {
      const controls = this.aggregateControlsTemplate.cloneNode(true);
      const activeTab = stampedRoot.dataset.widgetType === 'full-page-nps' ? '.btn-site-reviews' : '.btn-product-reviews';
      controls.querySelectorAll('li').forEach((item) => {
        item.classList.toggle('active', Boolean(item.querySelector(activeTab)));
      });
      stampedRoot.prepend(controls);
    }
    const emptyMessage = document.createElement('p');
    emptyMessage.dataset.ddReviewsEmpty = '';
    emptyMessage.textContent = 'No reviews in this view.';
    wrapper.appendChild(emptyMessage);
    this.markReviewsReady();
  }

  clearReviewsTimer() {
    window.clearTimeout(this.reviewsRetryTimer);
    this.reviewsRetryTimer = undefined;
  }

  markReviewsReady() {
    if (!this.reviewsPanel || !this.hasReviewsWidget) return;
    this.clearReviewsTimer();
    this.reviewsPanel.setAttribute('aria-busy', 'false');
    this.setAggregateReviewsReady(true);
    if (this.isAggregateReviews) {
      const controls = this.getStampedRoot()?.querySelector('.stamped-widget-buttons');
      if (controls) this.aggregateControlsTemplate = controls.cloneNode(true);
    }
    if (this.reviewsStatus) this.reviewsStatus.hidden = true;
  }

  markReviewsUnavailable() {
    if (!this.reviewsPanel) return;
    this.clearReviewsTimer();
    this.reviewsPanel.setAttribute('aria-busy', 'false');
    this.setAggregateReviewsReady(false);
    if (!this.reviewsStatus) return;
    this.reviewsStatus.textContent =
      window.ddReviewsLightboxStrings?.unavailable || this.reviewsStatus.textContent;
    this.reviewsStatus.hidden = false;
  }
}

if (!customElements.get('dd-pdp-lightbox')) customElements.define('dd-pdp-lightbox', DDPDPLightbox);

const getProductReviewsLightbox = () =>
  document.querySelector('dd-pdp-lightbox[data-product-id]:not([data-dd-reviews-lightbox-generic])');

const getGenericReviewsLightbox = () => {
  const existing = document.querySelector('dd-pdp-lightbox[data-dd-reviews-lightbox-generic]');
  if (existing) return existing;

  const template = document.querySelector('template[data-dd-reviews-lightbox-template]');
  if (!(template instanceof HTMLTemplateElement)) return null;

  const fragment = template.content.cloneNode(true);
  const lightbox = fragment.querySelector('dd-pdp-lightbox[data-dd-reviews-lightbox-generic]');
  if (!lightbox) return null;
  document.body.appendChild(fragment);
  return lightbox;
};

window.openReviewsLightbox = (trigger) => {
  const controlledDialog = trigger?.getAttribute?.('aria-controls');
  const controlledLightbox = controlledDialog
    ? document.getElementById(controlledDialog)?.closest('dd-pdp-lightbox')
    : null;
  const lightbox = trigger?.dataset?.ddReviewsScope === 'all'
    ? getGenericReviewsLightbox()
    : controlledLightbox || getProductReviewsLightbox() || getGenericReviewsLightbox();
  return lightbox?.openReviews(trigger) || false;
};

const removeSupersededGenericLightbox = () => {
  const productLightbox = getProductReviewsLightbox();
  const genericLightbox = document.querySelector('dd-pdp-lightbox[data-dd-reviews-lightbox-generic]');
  if (!productLightbox || !genericLightbox) return;
  genericLightbox.close(false);
  genericLightbox.remove();
};

if (!window.__ddPdpReviewsEventBound) {
  window.__ddPdpReviewsEventBound = true;
  document.addEventListener('dd:open-reviews', (event) => window.openReviewsLightbox(event.detail?.trigger));
  document.addEventListener('shopify:section:load', removeSupersededGenericLightbox);
}
